import { adminApi, clearToken } from '@/api/client';
import { Button, Result, Spin } from 'antd';
import { type ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

/**
 * 财务系统的员工身份守卫（统一身份版）。
 *
 * 用户面与后台面统一使用账户中心 JWT。员工身份的唯一权威来源是后端
 * `GET /admin-api/auth/me`（验账户令牌后按 account user id 查后台授权）。
 * 前端不用 JWT 里的 role 自行放行（伪造风险），
 * 一切以 /auth/me 为准:返回 active 角色 → 放行,否则 403。
 *
 * 注意：本守卫只判断「是不是有效员工账号」，具体财务功能的权限位
 * （finance.read / finance.claim.submit …）由 layouts/permissions 决定。
 */

/** admin core 的角色集合（来自 admin_core.admin_users.role）。 */
export const ADMIN_ROLES = ['super', 'ops', 'cs', 'finance'] as const;
export type AdminRole = string;

export interface AdminMe {
  id: number;
  account_user_id: number | null;
  username: string | null;
  email: string | null;
  role: AdminRole;
  status: 'active' | 'suspended';
  mfa_enabled?: boolean;
}

type Probe = 'loading' | 'ok' | 'unauthenticated' | 'denied' | 'unavailable';

/** 探一次 /admin-api/auth/me。FinanceLayout 常驻 → 整次加载只探一次。 */
export function useStaffMe(): { probe: Probe; me: AdminMe | null } {
  const [me, setMe] = useState<AdminMe | null>(null);
  const [probe, setProbe] = useState<Probe>('loading');
  useEffect(() => {
    let alive = true;
    adminApi
      .get<AdminMe>('/auth/me')
      .then((res) => {
        if (!alive) return;
        setMe(res.data);
        setProbe('ok');
      })
      .catch((error: { response?: { status?: number } }) => {
        if (!alive) return;
        const status = error.response?.status;
        if (status === 401) setProbe('unauthenticated');
        else if (status === 403) setProbe('denied');
        else setProbe('unavailable');
      });
    return () => {
      alive = false;
    };
  }, []);
  return { probe, me };
}

function isStaff(me: AdminMe | null): boolean {
  return !!me && me.status === 'active' && typeof me.role === 'string' && me.role.length > 0;
}

interface GuardState {
  status: 'loading' | 'allowed' | 'unauthenticated' | 'denied' | 'unavailable';
  me: AdminMe | null;
  role: string | null;
}

/** 解析后台访问权限,完全以后端 /auth/me 为准。 */
export function useStaffGuard(): GuardState {
  const { probe, me } = useStaffMe();
  if (probe === 'loading') return { status: 'loading', me, role: null };
  if (probe === 'unauthenticated') return { status: 'unauthenticated', me, role: null };
  if (probe === 'unavailable') return { status: 'unavailable', me, role: null };
  if (isStaff(me)) return { status: 'allowed', me, role: me?.role ?? null };
  return { status: 'denied', me, role: null };
}

/** 守卫包装：未授权时渲染 403，加载中渲染 Spin。 */
export function StaffGuard({ children }: { children: ReactNode }) {
  const { status } = useStaffGuard();
  const navigate = useNavigate();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '120px 0' }}>
        <Spin tip="正在校验员工身份…" size="large">
          <div style={{ width: 1, height: 1 }} />
        </Spin>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: { pathname: location.pathname } }} />;
  }

  if (status === 'denied') {
    return (
      <Result
        status="403"
        title="403"
        subTitle="抱歉，你没有有效的后台账号授权，或授权已过期。"
        extra={
          <Button
            type="primary"
            onClick={() => {
              clearToken();
              navigate('/login', { replace: true });
            }}
          >
            退出并重新登录
          </Button>
        }
      />
    );
  }

  if (status === 'unavailable') {
    return (
      <Result
        status="500"
        title="财务系统暂时不可用"
        subTitle="权限校验服务连接失败，这不是账号授权问题。请稍后重试。"
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            重新检查
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
}
